import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { initializeOpeningStock, getIngredients, getOpeningStockStatus } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle2, Upload, Download, Plus, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SmartIngredientInput from './SmartIngredientInput';
import { toCanonical, normalizeUnitKey, ACCEPTED_UNITS_FOR_UI } from '../lib/units';
import logger from '../lib/logger';
import { useDirtyExitGuard } from '../lib/useDirtyExitGuard';

/**
 * OpeningStockWizard (1OS-style upgrade)
 *
 * Doctrine:
 *   • Opening Stock = physical quantity baseline ONLY.
 *   • Price/cost comes from Expense Capture. We do NOT ask for it here.
 *
 * Features:
 *   • CSV / XLSX upload with column mapping + validation preview.
 *   • Manual table entry with SmartIngredientInput autocomplete.
 *   • Ingredient resolution: ingredient_id → exact name → fuzzy name.
 *   • Unresolved rows block final submit.
 *   • Confidence (LOW / MEDIUM / HIGH) + business_date (backdatable).
 *   • One-time-per-outlet enforced server-side (409 surfaced as toast).
 */

const TEMPLATE_HEADERS = ['ingredient_id', 'ingredient_name', 'quantity', 'unit', 'notes'];
const TEMPLATE_SAMPLE = [
  ['', 'Tomato', 12, 'kg', 'cold room'],
  ['', 'Milk',   20, 'l',  ''],
  ['', 'Eggs',   60, 'pcs', ''],
];

const newRow = () => ({
  _key: Math.random().toString(36).slice(2),
  ingredient_id: '',
  quantity: '',
  base_unit: 'gm',
  notes: '',
});

/** Very small Levenshtein for fuzzy name fallback (first-time upload). */
const dist = (a, b) => {
  a = a.toLowerCase(); b = b.toLowerCase();
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const c = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + c);
    }
  }
  return dp[a.length][b.length];
};

const resolveIngredient = (raw, ingredients) => {
  const byId = ingredients.find((i) => i.ingredient_id && i.ingredient_id === raw.ingredient_id);
  if (byId) return { match: byId, confidence: 'id' };
  const name = (raw.ingredient_name || '').trim().toLowerCase();
  if (!name) return { match: null, confidence: null };
  const exact = ingredients.find(
    (i) => ((i.ingredient_name || i.name || '').trim().toLowerCase() === name)
  );
  if (exact) return { match: exact, confidence: 'exact' };
  // Fuzzy: pick best within edit-distance threshold relative to length
  let best = null; let bestD = Infinity;
  for (const i of ingredients) {
    const candidate = (i.ingredient_name || i.name || '').trim().toLowerCase();
    if (!candidate) continue;
    const d = dist(name, candidate);
    if (d < bestD) { bestD = d; best = i; }
  }
  const threshold = Math.max(1, Math.floor(name.length * 0.35));
  if (best && bestD <= threshold) return { match: best, confidence: 'fuzzy', distance: bestD };
  return { match: null, confidence: null };
};

const guessMapping = (headers) => {
  const out = {};
  const norm = (s) => (s || '').toString().toLowerCase().replace(/[\s_-]/g, '');
  TEMPLATE_HEADERS.forEach((t) => {
    const target = norm(t);
    const found = headers.find((h) => norm(h) === target);
    if (found) out[t] = found;
  });
  // heuristics
  if (!out.ingredient_name) {
    const alt = headers.find((h) => /name|item/i.test(h));
    if (alt) out.ingredient_name = alt;
  }
  if (!out.quantity) {
    const alt = headers.find((h) => /qty|quant/i.test(h));
    if (alt) out.quantity = alt;
  }
  if (!out.unit) {
    const alt = headers.find((h) => /unit|uom/i.test(h));
    if (alt) out.unit = alt;
  }
  return out;
};

const OpeningStockWizard = ({ open, onClose, onSuccess, outletId }) => {
  const [step, setStep] = useState(1);
  const [understood, setUnderstood] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [openingBusinessDate, setOpeningBusinessDate] = useState(todayIso);
  const [confidence, setConfidence] = useState('LOW');
  const [rows, setRows] = useState([newRow()]);
  const [loading, setLoading] = useState(false);

  // Upload sub-state
  const [uploadStep, setUploadStep] = useState(null); // null | 'mapping' | 'preview'
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});

  // Override warning state — surfaced when outlet already has non-opening-stock
  // ledger activity. Only ORG_ADMIN / SUPER_ADMIN can tick the override box.
  const [statusMeta, setStatusMeta] = useState(null); // server status fields
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  // Idempotency key. Generated once per wizard session; sent with the submit
  // POST and any retries. Backend treats requests with the same id as the
  // same logical write — preventing the "60s gateway timeout → user retries
  // → 409 Conflict" confusion.
  const [requestId, setRequestId] = useState(null);

  // Inline error surfaced inside the wizard (in addition to toast) so the
  // operator can read the real backend detail without losing context.
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1); setUnderstood(false); setConfidence('LOW');
    setOpeningBusinessDate(todayIso); setRows([newRow()]);
    setUploadStep(null); setRawHeaders([]); setRawRows([]); setMapping({});
    setOverrideConfirmed(false); setSubmitError(null); setStatusMeta(null);
    // Fresh request_id per wizard session.
    setRequestId(
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `osi_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    );
    loadIngredients();
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadStatus = async () => {
    if (!outletId) return;
    try {
      const data = await getOpeningStockStatus(outletId);
      setStatusMeta(data || null);
    } catch (e) {
      logger.error('loadStatus failed', e);
      // Non-fatal — wizard still works; we just can't pre-warn.
    }
  };

  const loadIngredients = async () => {
    try {
      const data = await getIngredients(outletId);
      const list = Array.isArray(data) ? data : (data?.ingredients || []);
      // alphabetical
      list.sort((a, b) => String(a.ingredient_name || a.name || '').localeCompare(
        String(b.ingredient_name || b.name || ''), undefined, { sensitivity: 'base' }
      ));
      setIngredients(list);
    } catch (e) {
      logger.error('loadIngredients failed', e);
      toast.error('Failed to load ingredients');
    }
  };

  // ── rows state helpers ─────────────────────────────────────────
  const updateRow = (idx, patch) => setRows((r) => r.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const addRow = () => setRows((r) => [...r, newRow()]);
  const addFive = () => setRows((r) => [...r, ...Array.from({ length: 5 }, () => newRow())]);
  const removeRow = (idx) => setRows((r) => (r.length === 1 ? r : r.filter((_, i) => i !== idx)));

  // ── validation summary ────────────────────────────────────────
  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.ingredient_id && Number(r.quantity) > 0);
    const unresolved = rows.filter(
      (r) => Number(r.quantity) > 0 && !r.ingredient_id
    ).length;
    const invalidUnits = rows.filter(
      (r) => r.base_unit && toCanonical(1, r.base_unit).conversion_failed
    ).length;
    return { validCount: valid.length, unresolved, invalidUnits, total: rows.length };
  }, [rows]);

  // Outlet-level preconditions
  const hasExistingActivity = !!(statusMeta?.existing_non_opening_entries_count > 0);
  const canOverride = !!statusMeta?.can_override_opening_stock;
  const overrideBlocked = hasExistingActivity && !canOverride;
  const overrideRequiredUnchecked = hasExistingActivity && canOverride && !overrideConfirmed;

  const canSubmit = (
    !loading
    && summary.validCount > 0
    && summary.unresolved === 0
    && summary.invalidUnits === 0
    && !overrideBlocked
    && !overrideRequiredUnchecked
  );

  // ── Unsaved-progress detection ───────────────────────────────
  // Dirty = the operator has taken ANY action that would be discarded on close.
  // A fresh wizard sitting on step 1 with the default date is NOT dirty.
  const isDirty = useMemo(() => {
    if (step > 1) return true;
    if (uploadStep !== null) return true;
    if (rawHeaders.length > 0 || rawRows.length > 0) return true;
    if (Object.keys(mapping).length > 0) return true;
    if (confidence !== 'LOW') return true;
    if (openingBusinessDate !== todayIso) return true;
    if (overrideConfirmed) return true;
    // Any row that has typed content — id, qty, unit diff, notes, or a raw name
    const hasRowContent = rows.some((r) => (
      r.ingredient_id
      || (r.quantity !== '' && r.quantity !== null && r.quantity !== undefined)
      || r.notes
      || r._raw_name
      || (r.base_unit && r.base_unit !== 'gm')
    ));
    if (rows.length > 1 || hasRowContent) return true;
    return false;
  }, [step, uploadStep, rawHeaders, rawRows, mapping, confidence, openingBusinessDate, overrideConfirmed, rows, todayIso]);

  // requestClose: the single place where ALL close-intents land.
  // If dirty → show confirmation. If clean (or bypass latch set) → close now.
  const exitGuard = useDirtyExitGuard({
    isDirty,
    onConfirmClose: onClose,
    title: 'Exit inventory initialization?',
    body: 'You have unsaved inventory initialization progress. If you exit now, your uploaded file and mapped rows will be lost.',
    testIdPrefix: 'opening-stock-exit-confirm',
  });

  // ── Shared override warning renderer (visible across all step-2 sub-states) ──
  // Renders ONLY when hasExistingActivity. `compact` variant is used in the
  // mapping/preview sub-steps where we want a tighter strip without dropping
  // the checkbox.
  const renderOverrideWarning = (variant = 'full') => {
    if (!hasExistingActivity) return null;
    const isCompact = variant === 'compact';
    return (
      <div
        className={`rounded-md border ${isCompact ? 'p-2.5' : 'p-3'} ${overrideBlocked ? 'border-red-500/40 bg-red-500/10' : 'border-amber-500/40 bg-amber-500/10'}`}
        data-testid="opening-stock-existing-activity-warning"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0 ${overrideBlocked ? 'text-red-400' : 'text-amber-400'}`} />
          <div className="flex-1 text-sm">
            <p className={`font-medium ${overrideBlocked ? 'text-red-200' : 'text-amber-200'}`}>
              This outlet already has inventory activity.
            </p>
            {!isCompact && (
              <p className="text-zinc-300 mt-1">
                Opening Stock is normally the first stock anchor.
                {canOverride
                  ? ' As an Org Admin, you may override only if this is a controlled go-live / backdated baseline.'
                  : ' Only an Org Admin or Super Admin can override this.'}
              </p>
            )}
            <p className="text-xs text-zinc-400 mt-1">
              Existing ledger rows:{' '}
              <span className="font-mono text-zinc-200" data-testid="opening-stock-existing-count">
                {statusMeta?.existing_non_opening_entries_count}
              </span>
              {statusMeta?.existing_non_opening_entry_types?.length > 0 && (
                <> — types: <span className="font-mono text-zinc-200">
                  {statusMeta.existing_non_opening_entry_types.join(', ')}
                </span></>
              )}
            </p>
            {canOverride && (
              <label className={`${isCompact ? 'mt-2' : 'mt-3'} flex items-start gap-2 cursor-pointer select-none`}>
                <input
                  type="checkbox"
                  checked={overrideConfirmed}
                  onChange={(e) => setOverrideConfirmed(e.target.checked)}
                  className="mt-0.5"
                  data-testid="opening-stock-override-checkbox"
                />
                <span className={`text-sm ${overrideConfirmed ? 'text-emerald-200' : 'text-amber-200'}`}>
                  {isCompact
                    ? 'I confirm the admin override for this outlet (existing inventory activity).'
                    : 'I understand this outlet has existing inventory activity. I am intentionally setting the opening stock baseline (go-live / backdated) and want to override.'}
                </span>
              </label>
            )}
            {!canOverride && (
              <p className="text-xs text-red-300 mt-2 font-medium">
                You cannot proceed. Ask an Org Admin or Super Admin to initialize this outlet.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── template downloads ────────────────────────────────────────
  const downloadCsvTemplate = () => {
    const csv = '\uFEFF' + [TEMPLATE_HEADERS.join(','), ...TEMPLATE_SAMPLE.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'opening_stock_template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const downloadXlsxTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OpeningStock');
    XLSX.writeFile(wb, 'opening_stock_template.xlsx');
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      if (!json.length) { toast.error('File is empty'); return; }
      const headers = Object.keys(json[0]);
      setRawHeaders(headers);
      setRawRows(json);
      setMapping(guessMapping(headers));
      setUploadStep('mapping');
    } catch (e) {
      logger.error('upload parse failed', e);
      toast.error('Could not parse file. Please use CSV or XLSX.');
    }
  };

  const applyMapping = () => {
    const ingByName = new Map(ingredients.map((i) => [String(i.ingredient_name || i.name || '').trim().toLowerCase(), i]));
    const ingById = new Map(ingredients.map((i) => [i.ingredient_id, i]));
    const get = (row, target) => {
      const src = mapping[target];
      return src ? String(row[src] ?? '').trim() : '';
    };
    const next = [];
    for (const row of rawRows) {
      const raw = {
        ingredient_id: get(row, 'ingredient_id'),
        ingredient_name: get(row, 'ingredient_name'),
        quantity: get(row, 'quantity'),
        unit: get(row, 'unit'),
        notes: get(row, 'notes'),
      };
      const qty = Number(raw.quantity);
      if (!raw.ingredient_name && !raw.ingredient_id) continue;
      if (!isFinite(qty) || qty <= 0) continue;
      const r = resolveIngredient({ ingredient_id: raw.ingredient_id, ingredient_name: raw.ingredient_name }, ingredients);
      const match = r.match
        || (raw.ingredient_id && ingById.get(raw.ingredient_id))
        || (raw.ingredient_name && ingByName.get(raw.ingredient_name.toLowerCase()))
        || null;
      next.push({
        _key: Math.random().toString(36).slice(2),
        ingredient_id: match ? match.ingredient_id : '',
        _raw_name: raw.ingredient_name,
        _match_confidence: r.confidence,      // 'id' | 'exact' | 'fuzzy' | null
        _match_distance: r.distance ?? null,
        quantity: String(qty),
        base_unit: normalizeUnitKey(raw.unit) || raw.unit || 'gm',
        notes: raw.notes,
      });
    }
    if (!next.length) { toast.error('No valid rows found'); return; }
    setRows(next);
    setUploadStep('preview');
  };

  // ── submit ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      if (overrideBlocked) {
        toast.error('Only an Org Admin or Super Admin can initialize opening stock on an outlet with existing inventory activity.');
      } else if (overrideRequiredUnchecked) {
        toast.error('Tick the override confirmation to proceed.');
      } else if (summary.unresolved > 0) {
        toast.error(`${summary.unresolved} unresolved ingredient${summary.unresolved === 1 ? '' : 's'} — resolve before submitting.`);
      } else if (summary.invalidUnits > 0) {
        toast.error('One or more rows have invalid units.');
      } else {
        toast.error('No valid rows to submit.');
      }
      return;
    }
    setLoading(true);

    // Helper: if the backend write actually succeeded but the response was
    // lost (gateway timeout) or the retry hit the 409 guard, the canonical
    // signal is the status endpoint — initialized=true means we're done.
    const reconcileViaStatus = async (originalError) => {
      try {
        const status = await getOpeningStockStatus(outletId);
        if (status?.is_initialized) {
          toast.success('Opening Stock was initialized successfully. This outlet already has an opening stock anchor.');
          onSuccess?.({
            status: 'OK',
            outlet_id: outletId,
            init_id: status.init_id,
            inserted_count: status.entry_count,
            reconciled_after_timeout: true,
          });
          exitGuard.allowNextClose();
          onClose?.();
          return true;
        }
      } catch (statusErr) {
        logger.error('opening stock status reconcile failed', statusErr);
      }
      // Status says NOT initialized → genuine failure; surface original error.
      const detail = originalError?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || originalError?.message || 'Failed to initialize opening stock');
      logger.error('opening stock submit failed', originalError);
      setSubmitError(msg);
      toast.error(msg);
      return false;
    };

    try {
      const lines = rows
        .filter((r) => r.ingredient_id && Number(r.quantity) > 0)
        .map((r) => ({
          ingredient_id: r.ingredient_id,
          quantity: Number(r.quantity),
          base_unit: normalizeUnitKey(r.base_unit) || r.base_unit,
        }));
      const options = {
        opening_business_date: openingBusinessDate,
        opening_stock_confidence: confidence,
        request_id: requestId,
      };
      // Only send override=true when BOTH: server says we may AND the
      // operator explicitly ticked the confirmation box.
      if (hasExistingActivity && canOverride && overrideConfirmed) {
        options.override = true;
      }
      const res = await initializeOpeningStock(outletId, lines, options);
      const message = res?.idempotent_replay
        ? 'Opening Stock was already initialized for this outlet (idempotent replay).'
        : `Opening stock initialized — ${res?.inserted_count ?? lines.length} items.`;
      toast.success(message);
      onSuccess?.(res);
      // Bypass the unsaved-progress guard — this is an intentional successful close.
      exitGuard.allowNextClose();
      onClose?.();
    } catch (e) {
      // Treat both timeouts AND 409 the same way: ask the canonical status
      // endpoint whether the write actually landed.
      const status = e?.response?.status;
      const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
      const isNetwork = !e?.response && !isTimeout && /network/i.test(e?.message || '');
      if (status === 409 || isTimeout || isNetwork) {
        toast.message('Still checking whether initialization completed…');
        const ok = await reconcileViaStatus(e);
        if (!ok) return;
      } else {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to initialize opening stock');
        logger.error('opening stock submit failed', e);
        setSubmitError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── UI ────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) exitGuard.requestClose(); }}
    >
      <DialogContent
        className="bg-[#18181b] border-[#27272a] text-white max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="opening-stock-wizard"
        onEscapeKeyDown={exitGuard.onEscapeKeyDown}
        onPointerDownOutside={exitGuard.onPointerDownOutside}
        onInteractOutside={exitGuard.onInteractOutside}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Initialize Opening Stock</DialogTitle>
          <DialogDescription className="sr-only">
            Physical stock baseline only. Price / cost is captured by Expense Capture and is not entered here.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — Acknowledge */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-md">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-100">
                <p className="font-semibold text-amber-400">One-time, irreversible baseline</p>
                <ul className="mt-2 space-y-1 list-disc ml-4 text-amber-100/90">
                  <li>Captures the <strong>physical quantity</strong> you have on a given date.</li>
                  <li>Price / cost is <strong>not captured here</strong> — it comes from Expense Capture.</li>
                  <li>Corrections must use AUDIT_ADJUSTMENT (no edits / no re-initialization).</li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Stock as-of date</label>
                <input
                  type="date" max={todayIso} value={openingBusinessDate}
                  onChange={(e) => setOpeningBusinessDate(e.target.value)}
                  data-testid="opening-stock-business-date"
                  className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Confidence</label>
                <select
                  value={confidence} onChange={(e) => setConfidence(e.target.value)}
                  data-testid="opening-stock-confidence"
                  className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm"
                >
                  <option value="LOW">LOW — estimated / historical</option>
                  <option value="MEDIUM">MEDIUM — approximate but reviewed</option>
                  <option value="HIGH">HIGH — physically verified today</option>
                </select>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-zinc-300">
              <input
                type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)}
                data-testid="opening-stock-understood"
                className="mt-0.5"
              />
              I understand this baseline is immutable and corrections must go through an audit adjustment.
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-md text-sm"
              >Cancel</button>
              <button
                disabled={!understood}
                onClick={() => setStep(2)}
                data-testid="opening-stock-step-next"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md text-sm disabled:opacity-50"
              >Continue →</button>
            </div>
          </div>
        )}

        {/* Step 2 — Data entry + upload */}
        {step === 2 && uploadStep === null && (
          <div className="space-y-4 py-2">
            {/* Existing-activity warning — blocks or gates based on RBAC */}
            {renderOverrideWarning('full')}

            {/* Inline submit error (in addition to toast) */}
            {submitError && (
              <div
                className="rounded-md border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2"
                data-testid="opening-stock-submit-error"
                role="alert"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-red-200">Initialization failed</p>
                  <p className="text-red-100/90 mt-0.5">{submitError}</p>
                </div>
                <button
                  onClick={() => setSubmitError(null)}
                  className="text-red-300 hover:text-red-100 text-xs"
                  data-testid="opening-stock-submit-error-dismiss"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex flex-wrap gap-2">
                <button onClick={addRow} className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46] flex items-center gap-1" data-testid="opening-stock-add-row">
                  <Plus className="w-3 h-3" /> Add row
                </button>
                <button onClick={addFive} className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46]">+5 rows</button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={downloadCsvTemplate} className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46] flex items-center gap-1" data-testid="opening-stock-csv-template">
                  <Download className="w-3 h-3" /> CSV template
                </button>
                <button onClick={downloadXlsxTemplate} className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46] flex items-center gap-1" data-testid="opening-stock-xlsx-template">
                  <Download className="w-3 h-3" /> XLSX template
                </button>
                <label className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1 cursor-pointer" data-testid="opening-stock-upload-btn">
                  <Upload className="w-3 h-3" /> Upload CSV / XLSX
                  <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                         className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                </label>
              </div>
            </div>

            <div className="border border-[#27272a] rounded-md overflow-visible">
              <div className="grid grid-cols-12 gap-0 px-2 py-2 bg-[#0c0c0e] text-xs text-zinc-500 font-medium uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Ingredient</div>
                <div className="col-span-3">Quantity</div>
                <div className="col-span-2">Unit</div>
                <div className="col-span-1 text-right">×</div>
              </div>
              <div className="divide-y divide-[#27272a]">
                {rows.map((r, idx) => (
                  <div key={r._key} className="grid grid-cols-12 gap-2 px-2 py-2 items-center" data-testid={`opening-stock-row-${idx}`}>
                    <div className="col-span-1 text-xs text-zinc-500 font-mono">{idx + 1}</div>
                    <div className="col-span-5">
                      <SmartIngredientInput
                        ingredients={ingredients}
                        value={r.ingredient_id}
                        onChange={(id) => updateRow(idx, { ingredient_id: id })}
                        testIdPrefix={`opening-stock-ing-${idx}`}
                      />
                      {r._raw_name && !r.ingredient_id && (
                        <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Unresolved: “{r._raw_name}” — pick from list.
                        </p>
                      )}
                      {r._match_confidence === 'fuzzy' && r.ingredient_id && (
                        <p className="text-xs text-blue-300 mt-1">
                          Fuzzy match (distance {r._match_distance}). Verify before submitting.
                        </p>
                      )}
                    </div>
                    <div className="col-span-3">
                      <input
                        type="number" step="0.01" min="0" value={r.quantity}
                        onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                        data-testid={`opening-stock-qty-${idx}`}
                        className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={r.base_unit}
                        onChange={(e) => updateRow(idx, { base_unit: e.target.value })}
                        data-testid={`opening-stock-unit-${idx}`}
                        className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm"
                      >
                        {ACCEPTED_UNITS_FOR_UI.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:text-red-300" title="Remove" data-testid={`opening-stock-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {summary.unresolved > 0 && (
              <p className="text-xs text-amber-400 flex items-center gap-1" data-testid="opening-stock-unresolved-warning">
                <AlertCircle className="w-3 h-3" /> {summary.unresolved} row(s) have unresolved ingredients. Resolve before submitting.
              </p>
            )}

            {/* Inline reason when Review is gated by the override checkbox */}
            {overrideRequiredUnchecked && (
              <p
                className="text-xs text-amber-300 flex items-center gap-1"
                data-testid="opening-stock-override-required-reason"
              >
                <AlertCircle className="w-3 h-3" />
                This outlet already has inventory activity. Tick the admin override confirmation above before continuing.
              </p>
            )}
            {overrideBlocked && (
              <p
                className="text-xs text-red-300 flex items-center gap-1"
                data-testid="opening-stock-override-blocked-reason"
              >
                <AlertCircle className="w-3 h-3" />
                You cannot proceed — only an Org Admin or Super Admin can initialize this outlet because it already has inventory activity.
              </p>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <button onClick={() => setStep(1)} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm">Back</button>
              <button
                onClick={() => setStep(3)}
                disabled={
                  summary.validCount === 0
                  || summary.unresolved > 0
                  || summary.invalidUnits > 0
                  || overrideRequiredUnchecked
                  || overrideBlocked
                }
                data-testid="opening-stock-review-btn"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 font-bold rounded-md text-sm disabled:opacity-40"
              >Review →</button>
            </div>
          </div>
        )}

        {/* Step 2 (upload) — column mapping */}
        {step === 2 && uploadStep === 'mapping' && (
          <div className="space-y-4 py-2" data-testid="opening-stock-mapping">
            {renderOverrideWarning('compact')}
            <p className="text-sm text-zinc-400">Map your columns. Auto-detected where possible.</p>
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATE_HEADERS.map((target) => (
                <div key={target}>
                  <label className="block text-xs text-zinc-400 mb-1">
                    {target}
                    {['quantity', 'unit'].includes(target) && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <select
                    value={mapping[target] || ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [target]: e.target.value }))}
                    data-testid={`opening-stock-map-${target}`}
                    className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm"
                  >
                    <option value="">— skip —</option>
                    {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              At least one of <code>ingredient_id</code> or <code>ingredient_name</code> is required to resolve rows.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setUploadStep(null)} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm">Back</button>
              <button
                onClick={applyMapping}
                disabled={!(mapping.quantity && mapping.unit && (mapping.ingredient_id || mapping.ingredient_name))}
                data-testid="opening-stock-map-apply"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 font-bold rounded-md text-sm disabled:opacity-40"
              >Apply mapping →</button>
            </div>
          </div>
        )}

        {step === 2 && uploadStep === 'preview' && (
          <div className="space-y-3 py-2">
            {renderOverrideWarning('compact')}
            <p className="text-sm text-zinc-400">
              Review parsed rows. Unresolved rows are highlighted — you must map them to an ingredient before submit.
            </p>
            {/* reuse the same table by flipping uploadStep back to null */}
            <button onClick={() => setUploadStep(null)} className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold" data-testid="opening-stock-preview-continue">
              Continue to table →
            </button>
          </div>
        )}

        {/* Step 3 — Review */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div className="text-sm text-emerald-100">
                You are about to <strong>permanently initialize</strong> opening stock for this outlet. This cannot be undone.
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center" data-testid="opening-stock-review-summary">
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Total items</p>
                <p className="text-xl font-mono font-bold text-emerald-400" data-testid="opening-stock-review-items">{summary.validCount}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Quantity lines</p>
                <p className="text-xl font-mono font-bold">{summary.validCount}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Confidence</p>
                <p className="text-xl font-mono font-bold">{confidence}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Business date</p>
                <p className="text-sm font-mono font-bold">{openingBusinessDate}</p>
              </div>
            </div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm">Back</button>
              <div className="flex flex-col items-end gap-1">
                {(overrideRequiredUnchecked || overrideBlocked) && (
                  <p
                    className="text-xs text-amber-300 flex items-center gap-1"
                    data-testid="opening-stock-submit-blocked-reason"
                  >
                    <AlertCircle className="w-3 h-3" />
                    {overrideBlocked
                      ? 'You cannot initialize this outlet — admin override not permitted.'
                      : 'Go back and confirm the admin override before initializing.'}
                  </p>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  data-testid="opening-stock-submit-btn"
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 font-bold rounded-md text-sm disabled:opacity-40"
                >{loading ? 'Submitting…' : 'Initialize Opening Stock'}</button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {exitGuard.ConfirmDialog}
    </Dialog>
  );
};

export default OpeningStockWizard;
